import { ROLE_LABELS } from './roles';

const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

// Server limit is 20 invitations per user per hour (RATE_INVITE_CREATE_PER_HOUR).
export const BULK_INVITE_MAX = 20;

// "Loan Officer", "loan_officer" and "loan officer" all mean loan_officer.
const ROLE_BY_NAME = Object.fromEntries(
  Object.entries(ROLE_LABELS).flatMap(([key, label]) => [
    [key, key],
    [label.toLowerCase(), key],
    [label.toLowerCase().replace(/\s+/g, '_'), key],
  ]),
);

// Pasted emails or a CSV: one invite per line, "email" or "email, role". A line
// with no "@" (a header row, a blank) is skipped. Repeated emails keep the first.
// A role that is not in `allowedRoles` marks the row bad rather than falling
// back to the default, so a typo never hands out a different seat.
export function parseInviteList(text, { defaultRole, allowedRoles }) {
  const rows = [];
  const seen = new Set();
  const add = (rawEmail, roleCell, forcedError = null) => {
    const email = rawEmail.toLowerCase();
    if (seen.has(email)) return;
    seen.add(email);
    const role = roleCell ? ROLE_BY_NAME[roleCell.toLowerCase()] : defaultRole;
    let error = forcedError;
    if (!error && !EMAIL_RE.test(email)) error = 'Not a valid email';
    else if (!error && !role) error = `Unknown role "${roleCell}"`;
    else if (!error && !allowedRoles.includes(role)) error = `You can't invite a ${ROLE_LABELS[role] || role}`;
    rows.push({ email, role, error });
  };
  for (const line of String(text || '').split(/\r?\n/)) {
    let cells = line.split(/[,;\t]/).map((c) => c.trim().replace(/^"|"$/g, '')).filter(Boolean);
    cells = cells.flatMap((c) => ((c.match(/@/g) || []).length > 1 || cells.length === 1 ? c.split(/\s+/) : [c]));
    const emails = cells.filter((c) => c.includes('@'));
    if (emails.length > 1) {
      // A role next to several emails could belong to any of them: refuse rather than guess.
      const extra = cells.length > emails.length;
      emails.forEach((e) => add(e, null, extra ? 'Put one email per line to give it a role' : null));
    } else if (emails.length === 1) {
      // Cells before the email are names; the role is the first cell after it that names one.
      const after = cells.slice(cells.indexOf(emails[0]) + 1);
      add(emails[0], after.find((c) => ROLE_BY_NAME[c.toLowerCase()]) || after[0]);
    }
  }
  return rows;
}
